import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSJobComponent from '../../job.js';
import JobSchema from '../../../../models/job-schema.js';
import type Job from '../../../../models/job.js';
import type Company from '../../../../models/company.js';
import definition from '@job-schemas/public-ledger/credit-transfer-failed.json';
import { jobSchema } from '../../job-schema.js';
import { createLogger } from '../../../../ns-services/logger.js';
import { formatCredits } from '../../../../helpers/public-ledger-credits.js';
import { creditTransferFailedFigures } from '../../../../helpers/credit-pricing.js';
import { claimJob, isUnclaimed } from '../../../../helpers/job-claim.js';
import { lambdaRefusal } from '../../../../helpers/public-ledger-balance-api.js';
import { confirmRefund } from '../../../../helpers/public-ledger-credits-api.js';

import '../../../../ns-elements/ns-job-layout.js';
import '../../../../ns-elements/ns-detail-list.js';

const logger = createLogger('ns-public-ledger-credit-transfer-failed');

/**
 * The PUBLIC_LEDGER team's urgent card when a company's paid nodeStream credits couldn't be delivered after 3 attempts
 * (point 3). Automatic retries have stopped. "Transfer completed" runs the delivery again now. "Refunded" is for a refund
 * made by hand in Stripe: public-ledger-credits-confirm-refund completes the card only once Stripe shows the refund, then
 * retries stay stopped and the credits leave the company's "on the way" figure.
 */
@customElement(NSPublicLedgerCreditTransferFailed.is)
export default class NSPublicLedgerCreditTransferFailed extends NSJobComponent {
  static get is() {
    return 'ns-public-ledger-credit-transfer-failed';
  }

  static get schema(): JobSchema {
    return jobSchema(definition, {
      outcomes: {
        'transfer-completed': { type: 'button', label: 'Transfer completed' },
        // Completed by the confirm-refund lambda once Stripe shows the refund, never by an outcome button
        refunded: { type: 'hidden' },
      },
      icon: 'transaction',
    });
  }

  static styles = [
    ...super.styles,
    css`
      .job-panel ns-detail-list {
        margin: 0 0 var(--wa-space-m);
        --ns-detail-list-gap: var(--wa-space-2xs);
      }

      .last-error {
        overflow-wrap: anywhere;
      }
    `,
  ];

  @state()
  private __company?: Company;

  @state()
  private __busy = false;

  async connectedCallback() {
    await super.connectedCallback();
    const { companyId } = creditTransferFailedFigures(this._job?.metadata);
    if (!companyId || !this._db) return;
    try {
      this.__company = await this._db.getById<Company>('company', companyId);
    } catch (err) {
      logger.warn('Unable to load the company', err);
    }
  }

  private async __claim() {
    const token = this._auth?.token;
    if (!this._job || !token || this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await claimJob(token, this._job.id);
      this._job = { ...this._job, assignedTo: this._directory?.me?.id };
    } catch (err) {
      this.showError(lambdaRefusal(err, 'Unable to claim this job.'));
    } finally {
      this.__busy = false;
    }
  }

  private async __confirmRefund() {
    const job = this._job;
    if (!job || !this._auth?.token || this.__busy) return;
    this.__busy = true;
    this.clearError();
    this.loading('Checking the refund in Stripe');
    try {
      await confirmRefund(this._auth.token, { jobId: job.id });
      const updated = await this._db?.getById<Job>('job', job.id);
      if (updated) this._job = updated;
      this.loaded();
      if (updated?.status === 'COMPLETE') this._jobComplete();
      this.requestUpdate();
    } catch (err) {
      this.loaded();
      this.showError(lambdaRefusal(err, "The refund couldn't be confirmed. Please try again."));
    } finally {
      this.__busy = false;
    }
  }

  render() {
    const figures = creditTransferFailedFigures(this._job?.metadata);
    return html`
      <ns-job-layout>
        <p slot="intro">
          A company's paid nodeStream credits couldn't be delivered after 3 attempts, so automatic retries have stopped.
        </p>
        <div class="job-panel">
          <h3>The delivery</h3>
          <ns-detail-list
            .rows=${[
              { label: 'Company', value: this.__company?.name ?? figures.companyId ?? undefined },
              {
                label: 'Still to deliver',
                value: figures.creditsOwed !== null ? `${formatCredits(figures.creditsOwed)} credits` : undefined,
              },
            ]}
          ></ns-detail-list>
          <h3>Last error</h3>
          <p class="last-error">${figures.lastError ?? 'No error was recorded.'}</p>
        </div>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>What to do</h3>
          <p>Fix the cause, then press "Transfer completed" to run the delivery again now.</p>
          <p>
            To refund instead, refund the payment in Stripe first, then press "Refunded". It checks Stripe and is only
            accepted once the refund shows there; then the retries stop and the owed credits leave the company's "on the
            way" figure.
          </p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    const job = this._job;
    if (!job || job.status === 'COMPLETE') return nothing;
    if (isUnclaimed(job)) {
      return html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__claim()}>
          Claim
        </wa-button>
      `;
    }
    return html`
      <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__confirmRefund()}>
        Refunded
      </wa-button>
    `;
  }
}
